/**
 * receiptConfig — the store-owned "design" of the receipt.
 *
 * A single source of truth for WHICH fields print and any custom text, so the
 * thermal print, the in-billing preview, and (later) the digital copy all agree.
 *
 * Presets (standard / compact / detailed) are just starting points: they seed a
 * full config that the owner then tweaks field-by-field in Settings → Receipt
 * Designer. Paper width and the logo keep their own existing settings keys
 * (THERMAL_PRINTER_PAPER_WIDTH, INVOICE_SHOW_LOGO) and are passed separately.
 */

export type ReceiptPreset = 'standard' | 'compact' | 'detailed';

export const RECEIPT_PRESETS: ReceiptPreset[] = ['standard', 'compact', 'detailed'];

export interface ReceiptConfig {
  preset: ReceiptPreset;
  // header
  showAddress: boolean;
  showGstin: boolean;
  showPhone: boolean;
  headerTagline: string; // custom line under the store name ('' = none)
  // meta / body
  showCustomer: boolean;
  showHsn: boolean;
  showTaxBreakup: boolean; // per-tax lines vs. a single total
  /**
   * A rule between one item and the next.
   *
   * Worth having because an item is not always one line: the unit, HSN and a
   * line discount each get their own indented line on narrow paper, so three
   * items can print as nine lines with nothing showing where one ends. Costs
   * one line of paper per item, which is why `compact` leaves it off.
   */
  showItemSeparators: boolean;
  // footer
  footerMessage: string; // custom thank-you line
  showTerms: boolean;
  terms: string; // multi-line, one term per line
  showPoweredBy: boolean;
}

const DEFAULT_TERMS = 'Goods once sold are not returnable.\nSubject to local jurisdiction.';

/** Full default config for each preset — the seed the owner customizes. */
export const PRESET_CONFIGS: Record<ReceiptPreset, ReceiptConfig> = {
  standard: {
    preset: 'standard',
    showAddress: true,
    showGstin: true,
    showPhone: true,
    headerTagline: '',
    showCustomer: true,
    showHsn: true,
    showTaxBreakup: true,
    showItemSeparators: true,
    footerMessage: 'Thank you! Visit again.',
    showTerms: false,
    terms: DEFAULT_TERMS,
    showPoweredBy: true,
  },
  compact: {
    preset: 'compact',
    showAddress: false,
    showGstin: false,
    showPhone: true,
    headerTagline: '',
    showCustomer: false,
    showHsn: false,
    showTaxBreakup: false,
    showItemSeparators: false,
    footerMessage: 'Thank you!',
    showTerms: false,
    terms: DEFAULT_TERMS,
    showPoweredBy: true,
  },
  detailed: {
    preset: 'detailed',
    showAddress: true,
    showGstin: true,
    showPhone: true,
    headerTagline: '',
    showCustomer: true,
    showHsn: true,
    showTaxBreakup: true,
    showItemSeparators: true,
    footerMessage: 'Thank you! Visit again.',
    showTerms: true,
    terms: DEFAULT_TERMS,
    showPoweredBy: true,
  },
};

/** Coerce a stored string to a valid preset. */
export function asReceiptPreset(v: string | null | undefined): ReceiptPreset {
  return v === 'compact' || v === 'detailed' ? v : 'standard';
}

/** A fresh, complete config for a preset (defensive copy). */
export function defaultReceiptConfig(preset: ReceiptPreset = 'standard'): ReceiptConfig {
  return { ...PRESET_CONFIGS[preset] };
}

/** Minimal shape of the settings store we read from (keeps this file UI-free). */
export interface SettingsReader {
  getSetting: (key: string) => string;
}

/**
 * Resolve the effective ReceiptConfig from settings.
 *  1. RECEIPT_CONFIG (JSON) if present — merged over its preset defaults so a
 *     newly-added field is never `undefined`.
 *  2. else seed from the legacy RECEIPT_TEMPLATE preset.
 */
export function resolveReceiptConfig(store: SettingsReader): ReceiptConfig {
  const raw = store.getSetting('RECEIPT_CONFIG');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<ReceiptConfig>;
      const preset = asReceiptPreset(parsed.preset);
      return { ...PRESET_CONFIGS[preset], ...parsed, preset };
    } catch {
      /* corrupt JSON — fall through to legacy */
    }
  }
  return defaultReceiptConfig(asReceiptPreset(store.getSetting('RECEIPT_TEMPLATE')));
}

export function serializeReceiptConfig(config: ReceiptConfig): string {
  return JSON.stringify(config);
}
